import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Terms & Privacy acceptance gate.
 *
 * Shows for any signed-in user whose ``terms_version_accepted`` does not
 * equal the server-required ``terms_required_version``. Non-dismissible.
 * Users who refuse cannot pass into the platform after launch.
 */
export default function TermsAcceptanceGate() {
  const { user, refresh, logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const requiredVersion = user?.terms_required_version;
  const acceptedVersion = user?.terms_version_accepted;
  const needsAcceptance = !!user && !!requiredVersion && acceptedVersion !== requiredVersion;

  // Launch Mode gate: the mandatory lockout is only active once the admin
  // flips launch_mode → true from the Analytics dashboard. Before that
  // the modal stays hidden so current beta testers aren't blocked.
  // Admin preview: append ?preview_terms_gate=1 to any URL to force-show
  // the gate for testing without affecting other users.
  const launchOn = !!user?.launch_mode;
  const previewParam = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview_terms_gate") === "1";
  const shouldShow = needsAcceptance && (launchOn || previewParam);

  useEffect(() => {
    if (!shouldShow) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const suppressEsc = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", suppressEsc, true);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", suppressEsc, true);
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  const submit = async () => {
    if (!checked || busy) return;
    setBusy(true);
    try {
      await api.post("/users/me/accept-terms", { accepted: true, version: requiredVersion });
      await refresh();
      toast.success("Thanks — welcome to Last Bookmark.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-gate-title"
      data-testid="terms-acceptance-gate"
    >
      <div className="absolute inset-0 bg-[hsl(150_28%_8%)]/85 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-3xl border border-border bg-[hsl(var(--sb-cream))] p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full grid place-items-center bg-[hsl(var(--sb-moss))] text-[hsl(40_60%_96%)]">
            <FileText size={20} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[hsl(150_20%_32%)]">Required</div>
            <h2 id="terms-gate-title" className="font-serif text-2xl text-[hsl(150_32%_14%)]">
              Terms &amp; Privacy Policy
            </h2>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-[hsl(150_20%_28%)]">
          To use Last Bookmark you must read and accept our current{" "}
          <Link to="/terms" target="_blank" className="underline font-medium">Terms of Service</Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" className="underline font-medium">Privacy Policy</Link>.
          We track which version you accepted, when, and use that record to keep
          your account compliant.
        </p>

        <label className="mt-6 flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-[hsl(var(--sb-forest))]"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            data-testid="terms-gate-checkbox"
          />
          <span className="text-sm leading-relaxed text-[hsl(150_32%_14%)]">
            I have read and agree to the Terms of Service and Privacy Policy
            (version <span className="font-semibold">{requiredVersion}</span>).
          </span>
        </label>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); logout?.(); }}
            className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm border border-[hsl(150_20%_28%)]/30 text-[hsl(150_32%_14%)] hover:bg-[hsl(150_32%_14%)]/5"
            data-testid="terms-gate-decline"
          >
            Decline &amp; sign out
          </a>
          <button
            type="button"
            onClick={submit}
            disabled={!checked || busy}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)] hover:bg-[hsl(var(--sb-forest-2))] disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="terms-gate-accept"
          >
            <Check size={16} />
            {busy ? "Saving…" : "Accept & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
